import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    Calendar as CalendarIcon, 
    Plus, 
    MapPin,
    Clock,
    Users,
    ChevronLeft,
    ChevronRight,
    Check,
    X,
    HelpCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, isSameDay, parseISO, isBefore, startOfDay } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIVITY_TYPES = [
    { value: 'incident', label: 'Incident', color: 'bg-red-500' },
    { value: 'training', label: 'Training', color: 'bg-blue-500' },
    { value: 'meeting', label: 'Meeting', color: 'bg-purple-500' },
    { value: 'exercise', label: 'Exercise', color: 'bg-amber-500' },
];

const typeColors = {
    incident: 'bg-red-500 text-white',
    training: 'bg-blue-500 text-white',
    meeting: 'bg-purple-500 text-white',
    exercise: 'bg-amber-500 text-white',
};

const ActivityCard = ({ activity, onRespond, userId }) => {
    const userResponse = activity.responses?.find(r => r.user_id === userId);
    const attendingCount = activity.responses?.filter(r => r.status === 'attending').length || 0;
    
    return (
        <Card className={`border-l-4 activity-${activity.activity_type} hover-lift`}>
            <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                            <Badge className={typeColors[activity.activity_type]}>
                                {ACTIVITY_TYPES.find(t => t.value === activity.activity_type)?.label}
                            </Badge>
                            {userResponse && (
                                <Badge variant="outline" className={
                                    userResponse.status === 'attending' ? 'border-green-500 text-green-600' :
                                    userResponse.status === 'not_attending' ? 'border-red-500 text-red-600' :
                                    'border-amber-500 text-amber-600'
                                }>
                                    {userResponse.status === 'attending' ? 'Attending' :
                                     userResponse.status === 'not_attending' ? 'Not Attending' : 'Maybe'}
                                </Badge>
                            )}
                        </div>
                        
                        <h3 className="font-semibold text-lg">{activity.title}</h3>
                        
                        {activity.description && (
                            <p className="text-sm text-muted-foreground mt-1">{activity.description}</p>
                        )}
                        
                        <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                <span>{format(parseISO(activity.start_time), 'MMM d, h:mm a')}</span>
                            </div>
                            {activity.location && (
                                <div className="flex items-center gap-1">
                                    <MapPin className="w-4 h-4" />
                                    <span>{activity.location}</span>
                                </div>
                            )}
                            <div className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                <span>{attendingCount} attending</span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant={userResponse?.status === 'attending' ? 'default' : 'outline'}
                            className={userResponse?.status === 'attending' ? 'bg-green-600 hover:bg-green-700' : ''}
                            onClick={() => onRespond(activity.id, 'attending')}
                            data-testid={`respond-attending-${activity.id}`}
                        >
                            <Check className="w-4 h-4" />
                        </Button>
                        <Button
                            size="sm"
                            variant={userResponse?.status === 'maybe' ? 'default' : 'outline'}
                            className={userResponse?.status === 'maybe' ? 'bg-amber-500 hover:bg-amber-600' : ''}
                            onClick={() => onRespond(activity.id, 'maybe')}
                            data-testid={`respond-maybe-${activity.id}`}
                        >
                            <HelpCircle className="w-4 h-4" />
                        </Button>
                        <Button
                            size="sm"
                            variant={userResponse?.status === 'not_attending' ? 'default' : 'outline'}
                            className={userResponse?.status === 'not_attending' ? 'bg-red-600 hover:bg-red-700' : ''}
                            onClick={() => onRespond(activity.id, 'not_attending')}
                            data-testid={`respond-not-attending-${activity.id}`}
                        >
                            <X className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default function CalendarPage() {
    const { user, hasUnitAccess } = useAuth();
    const [activities, setActivities] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [loading, setLoading] = useState(true);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [newActivity, setNewActivity] = useState({
        title: '',
        description: '',
        activity_type: 'training',
        start_time: '',
        end_time: '',
        location: ''
    });
    
    // Get first day of current month (for restricting past month navigation)
    const currentMonthStart = startOfMonth(new Date());
    
    // Check if selected month is in the past
    const isSelectedMonthPast = isBefore(endOfMonth(selectedDate), startOfDay(new Date()));
    
    useEffect(() => {
        fetchActivities();
    }, [selectedDate]);
    
    const fetchActivities = async () => {
        // Don't fetch activities for past months
        if (isSelectedMonthPast) {
            setActivities([]);
            setLoading(false);
            return;
        }
        
        try {
            const start = startOfMonth(selectedDate).toISOString();
            const end = endOfMonth(selectedDate).toISOString();
            
            const response = await axios.get(`${API_URL}/activities`, {
                params: { start_date: start, end_date: end }
            });
            setActivities(response.data);
        } catch (error) {
            console.error('Failed to fetch activities:', error);
            toast.error('Failed to load activities');
        } finally {
            setLoading(false);
        }
    };
    
    const handleAddActivity = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/activities`, newActivity);
            toast.success('Activity created');
            setShowAddDialog(false);
            setNewActivity({
                title: '',
                description: '',
                activity_type: 'training',
                start_time: '',
                end_time: '',
                location: ''
            });
            fetchActivities();
        } catch (error) {
            toast.error('Failed to create activity');
        }
    };
    
    const handleRespond = async (activityId, status) => {
        try {
            await axios.post(`${API_URL}/activities/${activityId}/respond`, {
                activity_id: activityId,
                status: status
            });
            toast.success('Response updated');
            fetchActivities();
        } catch (error) {
            toast.error('Failed to update response');
        }
    };
    
    const activitiesOnSelectedDate = activities.filter(a => 
        isSameDay(parseISO(a.start_time), selectedDate)
    );
    
    const datesWithActivities = activities.map(a => parseISO(a.start_time));
    
    if (loading) {
        return (
            <div className="space-y-6">
                <div className="skeleton h-8 w-48 rounded"></div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="skeleton h-80 rounded-lg"></div>
                    <div className="lg:col-span-2 skeleton h-80 rounded-lg"></div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="space-y-6 animate-fade-in" data-testid="calendar-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold">Calendar</h2>
                    <p className="text-muted-foreground">
                        Schedule activities and respond to events
                    </p>
                </div>
                {hasUnitAccess() && (
                    <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                        <DialogTrigger asChild>
                            <Button className="ses-btn-primary gap-2" data-testid="add-activity-btn">
                                <Plus className="w-4 h-4" />
                                Add Activity
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                            <DialogHeader>
                                <DialogTitle>Create Activity</DialogTitle>
                                <DialogDescription>
                                    Schedule a new activity for the unit
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleAddActivity} className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Title</Label>
                                    <Input
                                        value={newActivity.title}
                                        onChange={(e) => setNewActivity({ ...newActivity, title: e.target.value })}
                                        placeholder="Activity title"
                                        required
                                        data-testid="activity-title"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Type</Label>
                                    <Select
                                        value={newActivity.activity_type}
                                        onValueChange={(value) => setNewActivity({ ...newActivity, activity_type: value })}
                                    >
                                        <SelectTrigger data-testid="activity-type">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {ACTIVITY_TYPES.map((type) => (
                                                <SelectItem key={type.value} value={type.value}>
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-3 h-3 rounded-full ${type.color}`}></div>
                                                        {type.label}
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Description</Label>
                                    <Textarea
                                        value={newActivity.description}
                                        onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                                        placeholder="Activity description (optional)"
                                        data-testid="activity-description"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Start Time</Label>
                                        <Input
                                            type="datetime-local"
                                            value={newActivity.start_time}
                                            onChange={(e) => setNewActivity({ ...newActivity, start_time: e.target.value })}
                                            required
                                            data-testid="activity-start"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>End Time</Label>
                                        <Input
                                            type="datetime-local"
                                            value={newActivity.end_time}
                                            onChange={(e) => setNewActivity({ ...newActivity, end_time: e.target.value })}
                                            data-testid="activity-end"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Location</Label>
                                    <Input
                                        value={newActivity.location}
                                        onChange={(e) => setNewActivity({ ...newActivity, location: e.target.value })}
                                        placeholder="Activity location (optional)"
                                        data-testid="activity-location"
                                    />
                                </div>
                                <DialogFooter>
                                    <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" className="ses-btn-primary" data-testid="submit-activity">
                                        Create Activity
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Calendar */}
                <Card>
                    <CardContent className="p-4">
                        <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={(date) => date && setSelectedDate(date)}
                            className="rounded-md"
                            disabled={{ before: currentMonthStart }}
                            fromMonth={currentMonthStart}
                            modifiers={{
                                hasActivity: datesWithActivities
                            }}
                            modifiersStyles={{
                                hasActivity: {
                                    fontWeight: 'bold',
                                    textDecoration: 'underline',
                                    textDecorationColor: 'hsl(var(--primary))',
                                    textUnderlineOffset: '4px'
                                }
                            }}
                        />
                    </CardContent>
                </Card>
                
                {/* Activities List */}
                <div className="lg:col-span-2 space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-lg">
                            {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                        </h3>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => {
                                    const prevDay = new Date(selectedDate);
                                    prevDay.setDate(prevDay.getDate() - 1);
                                    // Only allow if not going before current month
                                    if (!isBefore(prevDay, currentMonthStart)) {
                                        setSelectedDate(prevDay);
                                    }
                                }}
                                disabled={isBefore(new Date(selectedDate.getTime() - 86400000), currentMonthStart)}
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSelectedDate(new Date())}
                            >
                                Today
                            </Button>
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => {
                                    const nextDay = new Date(selectedDate);
                                    nextDay.setDate(nextDay.getDate() + 1);
                                    setSelectedDate(nextDay);
                                }}
                            >
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                    
                    {activitiesOnSelectedDate.length > 0 ? (
                        <div className="space-y-3">
                            {activitiesOnSelectedDate.map((activity) => (
                                <ActivityCard
                                    key={activity.id}
                                    activity={activity}
                                    onRespond={handleRespond}
                                    userId={user?.id}
                                />
                            ))}
                        </div>
                    ) : (
                        <Card>
                            <CardContent className="py-12 text-center">
                                <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                                <h3 className="font-medium text-lg">No activities</h3>
                                <p className="text-muted-foreground mt-1">
                                    No activities scheduled for this date
                                </p>
                            </CardContent>
                        </Card>
                    )}
                    
                    {/* Upcoming Activities */}
                    {activities.length > 0 && (
                        <Card className="mt-6">
                            <CardHeader>
                                <CardTitle>All Upcoming Activities</CardTitle>
                                <CardDescription>Events scheduled this month</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    {activities.slice(0, 10).map((activity) => (
                                        <div
                                            key={activity.id}
                                            className="flex items-center justify-between p-3 rounded-lg hover:bg-muted cursor-pointer"
                                            onClick={() => setSelectedDate(parseISO(activity.start_time))}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`w-3 h-3 rounded-full ${typeColors[activity.activity_type].split(' ')[0]}`}></div>
                                                <div>
                                                    <p className="font-medium">{activity.title}</p>
                                                    <p className="text-sm text-muted-foreground">
                                                        {format(parseISO(activity.start_time), 'MMM d, h:mm a')}
                                                    </p>
                                                </div>
                                            </div>
                                            <Badge variant="outline">
                                                {activity.responses?.filter(r => r.status === 'attending').length || 0} attending
                                            </Badge>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
}
